import { useEffect, useState } from 'react';
import { BREAKPOINTS } from '../lib/breakpoints';

export type ViewportSize = 'mobile' | 'tablet' | 'desktop';

function getViewportSize(): ViewportSize {
  // innerWidth can grow to the overflowing document width on mobile. Media
  // queries use the layout viewport, matching the CSS breakpoints even then.
  if (window.matchMedia(`(max-width: ${BREAKPOINTS.mobile - 1}px)`).matches) return 'mobile';
  if (window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 1}px)`).matches) return 'tablet';
  return 'desktop';
}

/**
 * Single source of truth for "which viewport bucket are we in?" in
 * JS. Paired with the MEDIA constants in lib/breakpoints so CSS
 * @media queries that want matching behaviour stay on the same pixel
 * values.
 *
 * SSR-safe: returns 'desktop' when `window` is undefined.
 */
export function useViewport(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(() => {
    if (typeof window === 'undefined') return 'desktop';
    return getViewportSize();
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setSize(getViewportSize());
    window.addEventListener('resize', handler);
    // Catch orientationchange on iOS explicitly — some browsers don't
    // fire `resize` on rotation until a subsequent layout event.
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  return size;
}

export function useIsMobile(): boolean {
  return useViewport() === 'mobile';
}

/** True below the desktop breakpoint (1024px). */
export function useIsBelowDesktop(): boolean {
  return useViewport() !== 'desktop';
}
